/**
 * Combined theme provider for the widget.
 * Consumes brand tokens from widget-core and provides MUI theme.
 *
 * This keeps MUI-specific code in the widget app (not in widget-core),
 * while providing a single provider for all theming needs.
 */

import { Theme, ThemeProvider } from '@mui/material/styles';
import { DerivedBrandTokens, useBrandTheme } from '@ptek/widget-core';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { createBrandedMuiTheme } from '../styles/createBrandedTheme';

interface WidgetThemeContextValue {
  /** MUI theme created from brand tokens */
  muiTheme: Theme;
  /** Brand tokens for direct access if needed */
  brand: DerivedBrandTokens;
}

const WidgetThemeContext = createContext<WidgetThemeContextValue | null>(null);

interface WidgetThemeProviderProps {
  children: ReactNode;
}

/**
 * Provides MUI theme to all children.
 * Must be rendered inside BrandThemeProvider (set up by widget-core's BaseWidget).
 *
 * Creates the MUI theme once from brand tokens and provides it via:
 * 1. MUI's ThemeProvider (for MUI components)
 * 2. WidgetThemeContext (for direct access to theme + brand tokens)
 */
export const WidgetThemeProvider = ({ children }: WidgetThemeProviderProps) => {
  const brand = useBrandTheme();

  const muiTheme = useMemo(() => createBrandedMuiTheme(brand), [brand]);

  const value = useMemo(() => ({ muiTheme, brand }), [muiTheme, brand]);

  return (
    <WidgetThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </WidgetThemeContext.Provider>
  );
};

/**
 * Access the widget theme context.
 * Provides both MUI theme and brand tokens.
 */
export const useWidgetTheme = (): WidgetThemeContextValue => {
  const context = useContext(WidgetThemeContext);

  if (!context) {
    throw new Error(
      'useWidgetTheme must be used within a WidgetThemeProvider.'
    );
  }

  return context;
};
